import React, { useState, useEffect, useCallback } from 'react';
import {
  Mail, Lock, Eye, EyeOff, Check, X, User, Building2, FileText, Briefcase,
  Hash, MapPin, Loader2, AlertCircle, Info,
} from 'lucide-react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import CountryPicker from './CountryPicker';
import TurnstileWidget from './TurnstileWidget';
import { isValidEmail, validateCredentialStrength } from '../lib/credentialPolicy';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// IMPORTANT: defined OUTSIDE the component so its reference is stable
// across re-renders. Otherwise React would remount the subtree (and
// focused inputs would lose focus) on every keystroke.
const SECTION_COLOR = {
  cyan:    'from-cyan-500 to-cyan-600',
  blue:    'from-blue-500 to-blue-600',
  violet:  'from-violet-500 to-violet-600',
  amber:   'from-amber-500 to-amber-600',
  emerald: 'from-emerald-500 to-emerald-600',
  slate:   'from-slate-500 to-slate-600',
};

function Section({ title, icon: Icon, children, color = 'cyan' }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2.5">
        <span className={`w-7 h-7 rounded-lg bg-gradient-to-br ${SECTION_COLOR[color] || SECTION_COLOR.cyan} flex items-center justify-center`}>
          <Icon className="w-3.5 h-3.5 text-white" />
        </span>
        {title}
      </h3>
      {children}
    </div>
  );
}

function FieldErr({ name, errors }) {
  if (!errors[name]) return null;
  return (
    <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1" data-testid={`reg-error-${name}`}>
      <AlertCircle className="w-3 h-3" /> {errors[name]}
    </p>
  );
}

const initialState = {
  // Account type
  is_company: false,
  // Personal
  first_name: '',
  surname: '',
  email: '',
  password: '',
  confirmPassword: '',
  country: '',
  country_code: '',
  phone: '',
  phone_country: '',
  // EVC
  evc_registered: false,
  evc_customer_id: '',
  // Company
  company_name: '',
  vat: '',
  address: '',
  zipcode: '',
  city: '',
  // Legal acceptances
  accepted_license: false,
  accepted_terms: false,
  accepted_refund: false,
  subscribed_newsletter: false,
};

export default function RegistrationForm({ onBackToLogin, onRegistered, googlePrefill = null }) {
  const [data, setData] = useState(() => (
    googlePrefill
      ? {
          ...initialState,
          email:      googlePrefill.email || '',
          first_name: googlePrefill.first_name || '',
          surname:    googlePrefill.surname || '',
        }
      : initialState
  ));
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverMsg, setServerMsg] = useState({ type: '', text: '' });
  const [captchaToken, setCaptchaToken] = useState(null);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('');
  const [signupTip, setSignupTip] = useState({ enabled: false, text: '' });

  const set = (field, value) => {
    setData((d) => ({ ...d, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
  };

  // Load Turnstile site key from public site config
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/config/public-site`);
        if (!r.ok || !alive) return;
        const cfg = await r.json();
        setTurnstileSiteKey(cfg.turnstile_site_key || '');
      } catch (_) { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  // Load the admin-authored "signup email tip" so we render the inline
  // hint directly below the Email field. Public endpoint — safe to call
  // before the customer is authenticated.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/config/signup-email-tip`);
        if (!r.ok || !alive) return;
        const data = await r.json();
        setSignupTip({
          enabled: !!data.enabled,
          text: data.text || '',
        });
      } catch (_) { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  const validate = useCallback(() => {
    const e = {};
    if (!data.first_name.trim()) e.first_name = 'Required';
    if (!data.surname.trim())    e.surname = 'Required';
    if (!isValidEmail(data.email)) e.email = 'Valid email required';
    const credentialErr = validateCredentialStrength(data.password);
    if (credentialErr) e.password = credentialErr;
    if (data.confirmPassword !== data.password) e.confirmPassword = 'Passwords do not match';
    if (!data.country.trim()) e.country = 'Select your country';
    if (!data.phone || !isValidPhoneNumber(data.phone)) e.phone = 'Valid phone with country code is required';

    if (data.evc_registered && !data.evc_customer_id.trim()) {
      e.evc_customer_id = 'EVC customer ID required when registered at EVC.de';
    }

    if (data.is_company) {
      if (!data.company_name.trim()) e.company_name = 'Required';
      if (!data.vat.trim())          e.vat          = 'VAT required';
      if (!data.address.trim())      e.address      = 'Required';
      if (!data.zipcode.trim())      e.zipcode      = 'Required';
      if (!data.city.trim())         e.city         = 'Required';
    }

    if (!data.accepted_license) e.accepted_license = 'Required';
    if (!data.accepted_terms)   e.accepted_terms   = 'Required';
    if (!data.accepted_refund)  e.accepted_refund  = 'Required';

    if (turnstileSiteKey && !captchaToken) e.captcha = 'Please complete the captcha';

    return e;
  }, [data, captchaToken, turnstileSiteKey]);

  const submit = async (ev) => {
    ev.preventDefault();
    setServerMsg({ type: '', text: '' });
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) {
      setServerMsg({ type: 'error', text: 'Please fix the highlighted fields.' });
      // scroll to first error
      const firstKey = Object.keys(e)[0];
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="reg-${firstKey}"], [data-testid="reg-${firstKey}-input"]`);
        if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        email: data.email.trim().toLowerCase(),
        password: data.password,
        first_name: data.first_name.trim(),
        surname: data.surname.trim(),
        country: data.country,
        phone: data.phone,
        phone_country: data.phone_country || null,
        evc_registered: data.evc_registered,
        evc_customer_id: data.evc_registered ? data.evc_customer_id.trim() : null,
        is_company: data.is_company,
        company_name: data.is_company ? data.company_name.trim() : null,
        vat: data.is_company ? data.vat.trim() : null,
        address: data.is_company ? data.address.trim() : null,
        zipcode: data.is_company ? data.zipcode.trim() : null,
        city: data.is_company ? data.city.trim() : null,
        accepted_license: data.accepted_license,
        accepted_terms:   data.accepted_terms,
        accepted_refund:  data.accepted_refund,
        subscribed_newsletter: data.subscribed_newsletter,
        captcha_token: captchaToken || '',
        google_credential: googlePrefill?.credential || undefined,
      };
      const r = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      // Read body as text first, then try to parse JSON. This way we still
      // see the original response even if it's not valid JSON.
      const rawText = await r.text();
      let body = {};
      try { body = rawText ? JSON.parse(rawText) : {}; } catch (_) { body = {}; }

      if (!r.ok) {
        // Captcha tokens are single-use. After ANY backend error the token
        // is burned, so refresh the widget so the customer can retry without
        // a confusing "captcha already used" loop.
        try {
          if (window.turnstile) {
            window.turnstile.reset();
          }
        } catch (_) { /* ignore */ }
        setCaptchaToken(null);

        // FastAPI may return `detail` as a string (our HTTPException) or as
        // an array of validation errors (Pydantic 422). Normalize both.
        let msg = '';
        const d = body && body.detail;
        if (typeof d === 'string' && d.trim()) {
          msg = d;
        } else if (Array.isArray(d) && d.length) {
          msg = d.map((it) => {
            const where = Array.isArray(it.loc) ? it.loc.filter((x) => x !== 'body').join('.') : '';
            return where ? `${where}: ${it.msg || ''}` : (it.msg || '');
          }).filter(Boolean).join(' · ');
        } else if (d && typeof d === 'object') {
          msg = JSON.stringify(d);
        } else if (rawText && rawText.length < 400 && !rawText.startsWith('{')) {
          msg = rawText;
        }
        if (!msg) {
          msg = `Registration failed (HTTP ${r.status}). Please refresh the captcha and try again.`;
        }
        setServerMsg({ type: 'error', text: msg });
        return;
      }
      setServerMsg({ type: 'success', text: body.message || 'Registration successful! Check your email to verify.' });
      if (onRegistered) onRegistered();
    } catch (err) {
      setServerMsg({ type: 'error', text: 'Network error — please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  // --- helpers ---
  const errCls = (field) =>
    errors[field]
      ? 'border-red-400 focus:ring-red-400 bg-red-50/30'
      : 'border-slate-300 focus:ring-cyan-500';

  // --- password strength meter (0..4) ---
  const passwordStrength = (() => {
    const p = data.password || '';
    if (!p) return { score: 0, label: '', color: 'slate' };
    let s = 0;
    if (p.length >= 8)                  s += 1;
    if (p.length >= 12)                 s += 1;
    if (/[a-z]/.test(p) && /[A-Z]/.test(p)) s += 1;
    if (/[0-9]/.test(p))                s += 1;
    if (/[^A-Za-z0-9]/.test(p))         s += 1;
    // cap at 4 visual segments
    s = Math.min(s, 4);
    const map = {
      0: { label: 'Too short',  color: 'red'     },
      1: { label: 'Weak',       color: 'red'     },
      2: { label: 'Fair',       color: 'amber'   },
      3: { label: 'Good',       color: 'blue'    },
      4: { label: 'Strong',     color: 'emerald' },
    };
    return { score: s, ...map[s] };
  })();

  const STRENGTH_BAR = {
    red:     'bg-red-500',
    amber:   'bg-amber-500',
    blue:    'bg-blue-500',
    emerald: 'bg-emerald-500',
    slate:   'bg-slate-200',
  };
  const STRENGTH_TEXT = {
    red:     'text-red-600',
    amber:   'text-amber-600',
    blue:    'text-blue-600',
    emerald: 'text-emerald-600',
    slate:   'text-slate-400',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header — uses flex-col on mobile so the "Back to login" link
              stays on its own row above the title/logo block instead of
              colliding with "Create Account" / "Join AUTO-REMAP" on
              narrow screens (~380px). On sm+ reverts to the original
              one-row layout (logo + title on the left, Back link on
              the right). */}
          <div className="bg-gradient-to-r from-cyan-600 to-blue-700 text-white px-4 sm:px-6 py-4 sm:py-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <img
                src="/logo.png"
                alt="AUTO-REMAP"
                width="479"
                height="102"
                className="h-10 sm:h-11 w-auto max-w-[140px] sm:max-w-[180px] rounded-lg bg-white px-2 py-1 shadow-md object-contain flex-shrink-0"
                data-testid="reg-header-logo"
              />
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold leading-tight">Create Account</h2>
                <p className="text-[11px] sm:text-xs text-cyan-100 mt-0.5">Join AUTO-REMAP</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onBackToLogin}
              className="self-end sm:self-auto text-xs text-white/90 hover:text-white whitespace-nowrap px-2 py-1 rounded hover:bg-white/10 transition"
              data-testid="reg-back-to-login"
            >
              ← Back to login
            </button>
          </div>

          <form onSubmit={submit} className="p-6 space-y-5" data-testid="registration-form">
            {/* Arrived here from "Sign in with Google" without an account:
                the address is already proven, so we say so and show what
                is still missing instead of an error. */}
            {googlePrefill && (
              <div
                className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-start gap-3"
                data-testid="reg-google-banner"
              >
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
                <div className="text-[12px] text-emerald-900 leading-relaxed">
                  <strong className="block text-[13px] mb-0.5">
                    Email verified by Google — {googlePrefill.email}
                  </strong>
                  You don&apos;t have an account yet. Your name and email are filled in already —
                  add a country, phone and password, and you&apos;re in. No confirmation email needed.
                </div>
              </div>
            )}

            {/* Account type toggle */}
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => set('is_company', false)}
                data-testid="reg-toggle-personal"
                className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                  !data.is_company
                    ? 'border-cyan-500 bg-cyan-50 shadow-md'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <User className={`w-6 h-6 ${!data.is_company ? 'text-cyan-600' : 'text-slate-400'}`} />
                <span className={`text-sm font-semibold ${!data.is_company ? 'text-cyan-700' : 'text-slate-600'}`}>
                  Personal account
                </span>
              </button>
              <button
                type="button"
                onClick={() => set('is_company', true)}
                data-testid="reg-toggle-company"
                className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                  data.is_company
                    ? 'border-cyan-500 bg-cyan-50 shadow-md'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <Building2 className={`w-6 h-6 ${data.is_company ? 'text-cyan-600' : 'text-slate-400'}`} />
                <span className={`text-sm font-semibold ${data.is_company ? 'text-cyan-700' : 'text-slate-600'}`}>
                  Company account
                </span>
              </button>
            </div>

            {/* Personal info */}
            <Section title="Your Information" icon={User} color="cyan">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">First name *</label>
                  <input
                    type="text" value={data.first_name}
                    onChange={(e) => set('first_name', e.target.value)}
                    data-testid="reg-first_name-input"
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errCls('first_name')}`}
                  />
                  <FieldErr name="first_name" errors={errors} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Surname *</label>
                  <input
                    type="text" value={data.surname}
                    onChange={(e) => set('surname', e.target.value)}
                    data-testid="reg-surname-input"
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errCls('surname')}`}
                  />
                  <FieldErr name="surname" errors={errors} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <input
                      type="email" value={data.email}
                      onChange={(e) => set('email', e.target.value)}
                      readOnly={!!googlePrefill}
                      data-testid="reg-email-input"
                      className={`w-full pl-9 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errCls('email')} ${googlePrefill ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : ''}`}
                    />
                  </div>
                  <FieldErr name="email" errors={errors} />
                  {signupTip.enabled && signupTip.text.trim() && (
                    <div
                      className="mt-2 rounded-lg border border-cyan-200 bg-cyan-50 px-3 py-2 text-[12px] text-cyan-900 flex items-start gap-2"
                      data-testid="reg-signup-email-tip"
                    >
                      <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-cyan-600" />
                      <span>{signupTip.text}</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Password *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'} value={data.password}
                      onChange={(e) => set('password', e.target.value)}
                      data-testid="reg-password-input"
                      className={`w-full pl-9 pr-9 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errCls('password')}`}
                    />
                    <button type="button" onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-2 top-2.5 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <FieldErr name="password" errors={errors} />
                  {data.password && (
                    <div className="mt-2" data-testid="reg-password-strength">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4].map((seg) => (
                          <div
                            key={seg}
                            className={`h-1.5 flex-1 rounded-full transition-colors ${
                              passwordStrength.score >= seg
                                ? STRENGTH_BAR[passwordStrength.color]
                                : 'bg-slate-200'
                            }`}
                          />
                        ))}
                      </div>
                      <div className="flex justify-between mt-1.5">
                        <p className={`text-[11px] font-semibold ${STRENGTH_TEXT[passwordStrength.color]}`}>
                          {passwordStrength.label || ' '}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          {data.password.length < 8 ? `${8 - data.password.length} more chars` : 'Use upper/lower, digits & symbols'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Confirm password *</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <input
                      type={showPassword ? 'text' : 'password'} value={data.confirmPassword}
                      onChange={(e) => set('confirmPassword', e.target.value)}
                      data-testid="reg-confirmPassword-input"
                      className={`w-full pl-9 pr-9 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errCls('confirmPassword')}`}
                    />
                    {data.confirmPassword && (
                      data.password === data.confirmPassword
                        ? <Check className="absolute right-2 top-3 w-4 h-4 text-emerald-500" />
                        : <X className="absolute right-2 top-3 w-4 h-4 text-red-500" />
                    )}
                  </div>
                  <FieldErr name="confirmPassword" errors={errors} />
                </div>
              </div>
            </Section>

            {/* Country + Phone */}
            <Section title="Location & Contact" icon={MapPin} color="blue">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Country *</label>
                  <CountryPicker
                    value={data.country}
                    onChange={(name, code) => {
                      set('country', name);
                      set('country_code', code);
                      // If user hasn't entered a phone yet, prime the phone-input default country.
                      if (!data.phone) set('phone_country', code);
                    }}
                    error={!!errors.country}
                    testid="reg-country"
                  />
                  <FieldErr name="country" errors={errors} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Phone number *</label>
                  <div
                    data-testid="reg-phone-input"
                    className={`phone-wrap rounded-lg border focus-within:ring-2 ${errors.phone ? 'border-red-400 focus-within:ring-red-400 bg-red-50/30' : 'border-slate-300 focus-within:ring-cyan-500'}`}
                  >
                    <PhoneInput
                      international
                      defaultCountry={data.phone_country || data.country_code || 'DE'}
                      value={data.phone}
                      onChange={(v) => set('phone', v || '')}
                      onCountryChange={(c) => set('phone_country', c || '')}
                      countryCallingCodeEditable={false}
                      placeholder="Enter phone number"
                    />
                  </div>
                  <FieldErr name="phone" errors={errors} />
                </div>
              </div>
            </Section>

            {/* EVC.de */}
            <Section title="EVC.de Registration" icon={FileText} color="violet">
              <p className="text-xs text-slate-500 mb-3">
                Are you registered at <a href="https://evc.de" target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">EVC.de</a>? If yes, share your WinOLS / EVC customer ID.
              </p>
              <div className="flex gap-2 mb-3">
                <button
                  type="button" onClick={() => set('evc_registered', false)}
                  data-testid="reg-evc-no"
                  className={`flex-1 py-2 rounded-lg border-2 text-sm font-semibold transition ${
                    !data.evc_registered ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >No</button>
                <button
                  type="button" onClick={() => set('evc_registered', true)}
                  data-testid="reg-evc-yes"
                  className={`flex-1 py-2 rounded-lg border-2 text-sm font-semibold transition ${
                    data.evc_registered ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                >Yes</button>
              </div>
              {data.evc_registered && (
                <div data-testid="reg-evc-id-block">
                  <label className="block text-xs font-semibold text-slate-700 mb-1">WinOLS / EVC customer ID *</label>
                  <input
                    type="text" value={data.evc_customer_id}
                    onChange={(e) => set('evc_customer_id', e.target.value)}
                    data-testid="reg-evc_customer_id-input"
                    className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errCls('evc_customer_id')}`}
                  />
                  <FieldErr name="evc_customer_id" errors={errors} />
                </div>
              )}
            </Section>

            {/* Company section (conditional) */}
            {data.is_company && (
              <Section title="Company / Invoicing details" icon={Briefcase} color="amber">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Company name *</label>
                    <input
                      type="text" value={data.company_name}
                      onChange={(e) => set('company_name', e.target.value)}
                      data-testid="reg-company_name-input"
                      className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errCls('company_name')}`}
                    />
                    <FieldErr name="company_name" errors={errors} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">VAT number *</label>
                    <input
                      type="text" value={data.vat}
                      onChange={(e) => set('vat', e.target.value)}
                      data-testid="reg-vat-input"
                      className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errCls('vat')}`}
                    />
                    <FieldErr name="vat" errors={errors} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Address / PO Box *</label>
                    <input
                      type="text" value={data.address}
                      onChange={(e) => set('address', e.target.value)}
                      data-testid="reg-address-input"
                      className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errCls('address')}`}
                    />
                    <FieldErr name="address" errors={errors} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Zipcode *</label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <input
                        type="text" value={data.zipcode}
                        onChange={(e) => set('zipcode', e.target.value)}
                        data-testid="reg-zipcode-input"
                        className={`w-full pl-9 pr-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errCls('zipcode')}`}
                      />
                    </div>
                    <FieldErr name="zipcode" errors={errors} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">City *</label>
                    <input
                      type="text" value={data.city}
                      onChange={(e) => set('city', e.target.value)}
                      data-testid="reg-city-input"
                      className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 ${errCls('city')}`}
                    />
                    <FieldErr name="city" errors={errors} />
                  </div>
                </div>
              </Section>
            )}

            {/* Legal acceptances */}
            <Section title="Legal Agreements" icon={FileText} color="emerald">
              {[
                { key: 'accepted_license', label: 'I agree with the Licence agreement', link: '/license', required: true },
                { key: 'subscribed_newsletter', label: "Yes, I would like to receive your Newsletter", required: false },
                { key: 'accepted_terms',   label: 'I accept the Terms of service', link: '/terms', required: true },
                { key: 'accepted_refund',  label: 'I accept the Refund policy',    link: '/refund-policy', required: true },
              ].map((row) => {
                const errored = row.required && errors[row.key];
                return (
                  <label
                    key={row.key}
                    className={`flex items-start gap-2.5 p-2.5 rounded-lg border mb-2 cursor-pointer transition ${
                      errored
                        ? 'border-red-400 bg-red-50/40'
                        : data[row.key]
                          ? 'border-emerald-300 bg-emerald-50/40'
                          : 'border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid={`reg-${row.key}`}
                  >
                    <input
                      type="checkbox" checked={data[row.key]}
                      onChange={(e) => set(row.key, e.target.checked)}
                      data-testid={`reg-${row.key}-input`}
                      className="mt-0.5 w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                    />
                    <span className="text-sm text-slate-700 flex-1">
                      {row.label}
                      {row.required && <span className="text-red-500 ml-1">*</span>}
                      {row.link && (
                        <> — <a href={row.link} target="_blank" rel="noopener noreferrer" className="text-cyan-600 hover:underline">read here</a></>
                      )}
                      {!row.required && <span className="text-slate-400 ml-1 text-xs">(optional)</span>}
                    </span>
                  </label>
                );
              })}
            </Section>

            {/* Captcha */}
            <Section title="Verification" icon={Lock} color="slate">
              {turnstileSiteKey ? (
                <>
                  <TurnstileWidget
                    siteKey={turnstileSiteKey}
                    onVerify={(t) => { setCaptchaToken(t); setErrors((e) => ({ ...e, captcha: undefined })); }}
                    testid="reg-captcha"
                  />
                  {errors.captcha && (
                    <p className="text-[11px] text-red-600 mt-2 text-center" data-testid="reg-error-captcha">
                      {errors.captcha}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-xs text-slate-400 text-center">Captcha is not configured. The registration will still work.</p>
              )}
            </Section>

            {/* Server message */}
            {serverMsg.text && (
              <div className={`p-3 rounded-lg text-sm ${
                serverMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
              }`} data-testid="reg-server-msg">
                {serverMsg.text}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit" disabled={submitting}
              data-testid="reg-submit"
              className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold rounded-lg hover:shadow-xl disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {submitting ? 'Creating account…' : 'Create account'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          © 2026 AR / AUTO-REMAP
        </p>
      </div>

      <style>{`
        /* Hide ugly default border of react-phone-number-input — we provide our own */
        .phone-wrap .PhoneInput { padding: 0.5rem 0.75rem; }
        .phone-wrap .PhoneInputCountrySelect { background: transparent; }
        .phone-wrap input.PhoneInputInput {
          border: 0; outline: none; background: transparent;
          font-size: 0.875rem; padding: 0.25rem 0; flex: 1;
        }
      `}</style>
    </div>
  );
}
